'use client'

import { useEffect, useState } from 'react'
import { useAuth, useOrganization } from '@clerk/nextjs'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ClipboardDocumentIcon, ExclamationTriangleIcon, KeyIcon } from '@heroicons/react/24/outline'
import Toast from './toast'

export default function OrgAuthSettings() {
  const { getToken } = useAuth()
  const { membership, organization } = useOrganization()
  const baseUrl = process.env.NEXT_PUBLIC_BACKEND_API || 'http://localhost:3000'

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [currentToken, setCurrentToken] = useState(null)
  const [createdAt, setCreatedAt] = useState(null)
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' })
  const [confirming, setConfirming] = useState(false)
  const [slugConfirmation, setSlugConfirmation] = useState('')

  const isOrgAdmin = membership?.role === 'org:admin'
  const actionLabel = currentToken ? 'Rotate API key' : 'Generate API key'

  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true)
        const token = await getToken()
        const res = await fetch(`${baseUrl}/api/secure/client-token`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error(`Failed to fetch API key (${res.status})`)
        const data = await res.json()
        setCurrentToken(data.token)
        setCreatedAt(data.createdAt)
      } catch (err) {
        console.error('Error fetching API key:', err)
        setToast({ show: true, type: 'error', message: 'Failed to load API key' })
      } finally {
        setLoading(false)
      }
    }
    fetchToken()
  }, [baseUrl, getToken])

  const rotateToken = async () => {
    try {
      setSaving(true)
      const authToken = await getToken()
      const res = await fetch(`${baseUrl}/api/secure/client-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new Error(errData.error || `Failed to rotate API key (${res.status})`)
      }
      const data = await res.json()
      setCurrentToken(data.token)
      setCreatedAt(data.createdAt)
      setToast({
        show: true,
        type: 'success',
        message: currentToken ? 'API key rotated' : 'API key generated',
      })
    } catch (err) {
      console.error('Error rotating API key:', err)
      setToast({ show: true, type: 'error', message: err.message || 'Failed to rotate API key' })
    } finally {
      setSaving(false)
      setConfirming(false)
      setSlugConfirmation('')
    }
  }

  const copyToken = async () => {
    if (!currentToken) return

    try {
      await navigator.clipboard.writeText(currentToken)
      setToast({ show: true, type: 'success', message: 'API key copied' })
    } catch (err) {
      console.error('Error copying API key:', err)
      setToast({ show: true, type: 'error', message: 'Failed to copy API key' })
    }
  }

  const closeConfirmation = () => {
    if (saving) return
    setConfirming(false)
    setSlugConfirmation('')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#6c47ff]" />
      </div>
    )
  }

  if (!isOrgAdmin) {
    return (
      <div className="w-full max-w-2xl p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-900">Admin access required</h3>
          <p className="mt-1 text-sm text-amber-800">
            Only organization administrators can view or rotate the API key.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-2xl p-6 text-gray-900">
      <Toast
        type={toast.type}
        message={toast.message}
        isVisible={toast.show}
        onDismiss={() => setToast({ ...toast, show: false })}
      />

      <div>
        <h2 className="text-lg font-semibold tracking-tight">API key</h2>
        <p className="mt-1 text-sm text-gray-500">
          Manage the API key used by your organization&apos;s external links and integrations.
        </p>
      </div>

      <section className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-start gap-3 px-5 py-4">
          <div className="mt-0.5 rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-600">
            <KeyIcon className="h-4 w-4" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">Organization API key</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Use this key to authenticate requests to HazCams APIs and protected media links.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 px-5 py-4">
          {currentToken ? (
            <>
              <label htmlFor="organization-api-key" className="mb-1.5 block text-xs font-medium text-gray-600">
                API key
              </label>
              <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm focus-within:border-[#6c47ff] focus-within:ring-1 focus-within:ring-[#6c47ff]">
                <input
                  id="organization-api-key"
                  type="text"
                  readOnly
                  value={currentToken}
                  className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 font-mono text-sm text-gray-800 outline-none"
                />
                <button
                  type="button"
                  onClick={copyToken}
                  className="flex items-center gap-1.5 border-l border-gray-300 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                  Copy
                </button>
              </div>
              {createdAt && (
                <p className="mt-2 text-xs text-gray-500">
                  Created {new Date(createdAt).toLocaleString()}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">No API key has been generated for this organization.</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-gray-50 px-5 py-3">
          <p className="text-xs text-gray-500">
            {currentToken
              ? 'A new key is generated automatically when you rotate it.'
              : 'Generate an API key for your organization.'}
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={() => currentToken ? setConfirming(true) : rotateToken()}
            className="shrink-0 rounded-lg bg-[#6c47ff] px-3 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-[#5b3fd1] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLabel}
          </button>
        </div>
      </section>

      <Dialog open={confirming} onClose={closeConfirmation} className="relative z-[10000]">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-[1px] transition-opacity duration-200 data-closed:opacity-0"
        />
        <div className="fixed inset-0 z-[10000] overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <DialogPanel
              transition
              className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition duration-200 data-closed:scale-95 data-closed:opacity-0"
            >
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  if (slugConfirmation === organization?.slug) rotateToken()
                }}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                      <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <DialogTitle className="text-base font-semibold text-gray-900">
                        Rotate API key?
                      </DialogTitle>
                      <p className="mt-1.5 text-sm leading-6 text-gray-600">
                        The current key will stop working immediately. Any links or integrations using it must be updated with the newly generated key.
                      </p>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="organization-slug-confirmation" className="block text-sm font-medium text-gray-700">
                      Type <span className="font-mono font-semibold text-gray-900">{organization?.slug}</span> to confirm
                    </label>
                    <input
                      id="organization-slug-confirmation"
                      type="text"
                      value={slugConfirmation}
                      onChange={(event) => setSlugConfirmation(event.target.value)}
                      placeholder="Organization slug"
                      autoComplete="off"
                      autoFocus
                      className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 shadow-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={closeConfirmation}
                    className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || slugConfirmation !== organization?.slug}
                    className="rounded-lg bg-red-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? 'Rotating...' : 'Rotate API key'}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
